import apiClient from './api'
import type { User } from '../types'
import type {
  AmortizationTable,
  InProgressLeaseApplication,
} from '../types/management.types'

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  success: boolean
  data: {
    id: string
    email: string
    rol: string[]
    deleted: boolean
    active: boolean
    mustChangePassword?: boolean
    autoSendInvitation?: boolean
    token: string
    personType: string
    rfc: string
    requiresTermsAcceptance: boolean
    requiresPrivacyAcceptance: boolean
  }
}

export interface AuthServiceResponse {
  user: User
  token: string
  mustChangePassword?: boolean
}

// Forma laxa de una credit application tal como la regresa
// GET /credit-application dentro de `data.creditApps` — no se modela todo
// el payload anidado (request_data, user, etc.), solo lo que el frontend
// necesita leer.
// Estado de consultas (buró/SAT) y de qué pantallas del onboarding debe ver
// el usuario. Viene top-level en la respuesta de GET /credit-application
// (junto a creditApps), no dentro de cada solicitud.
export interface ConsultaStatus {
  fresh: boolean
  requiresClientAction: boolean
  lastConsultaDate: string | null
}

export interface ConsultasStatus {
  buro: ConsultaStatus
  sat: ConsultaStatus
  screens: Record<string, boolean>
}

export interface CreditApplicationSummary {
  id: string
  status: string
  cotizacion?: { id: string; [key: string]: any }
  prospectoQuote?: { id: string; [key: string]: any }
  guarantorEnabled?: boolean
  guarantorUserId?: string | null
  progressURL?: string
  [key: string]: any
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token'
  private readonly USER_KEY = 'user_data'
  private readonly MUST_CHANGE_PASSWORD_KEY = 'must_change_password'
  private readonly PROGRESS_URL_KEY = 'active_progress_url'
  private readonly HAS_ACTIVE_PROGRESS_KEY = 'has_active_progress'

  async loginGuarantor(
    token: string
  ): Promise<{ success: boolean; data: any }> {
    try {
      const response = await apiClient.post(
        '/credit-application/guarantor/validate-token',
        {
          token,
        }
      )

      if (response.data?.success) {
        const {
          accessToken,
          user,
          requiresTermsAcceptance,
          requiresPrivacyAcceptance,
        } = response.data.data
        this.setToken(accessToken)
        this.setUser({
          ...user,
          requiresTermsAcceptance: requiresTermsAcceptance,
          requiresPrivacyAcceptance: requiresPrivacyAcceptance,
        })
      }

      return response.data
    } catch (error: any) {
      throw error
    }
  }

  // Realizar login
  async login(credentials: LoginCredentials): Promise<AuthServiceResponse> {
    try {
      const body = {
        ...credentials,
        origin: 'web',
      }
      const response = await apiClient.post<LoginResponse>(
        '/auth/login/user',
        body
      )

      if (!response.data.success) {
        throw new Error('Login fallido')
      }

      const { data } = response.data

      // Validar mustChangePassword del token decodificado
      let mustChangePasswordFromToken = false
      try {
        const payload = JSON.parse(atob(data.token.split('.')[1]))
        mustChangePasswordFromToken = !!payload.mustChangePassword
      } catch {}

      // Invertir la lógica según tu requerimiento:
      // true en el response significa false en el front, false en el response es true en el front
      // Por lo tanto, si mustChangePasswordFromToken es true, el usuario NO debe cambiar contraseña
      // Si mustChangePasswordFromToken es false, el usuario SÍ debe cambiar contraseña
      const mustChangePassword = !mustChangePasswordFromToken

      // Crear objeto User desde la respuesta
      const user: User = {
        id: data.id,
        email: data.email,
        firstName: '', // La API no devuelve estos campos, podrías necesitar otro endpoint
        lastName: '',
        phone: '',
        hasPassword: true,
        isActive: data.active,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        roles: data.rol,
        personType: data.personType,
        rfc: data.rfc,
        autoSendInvitation: data.autoSendInvitation,
        requiresTermsAcceptance: data.requiresTermsAcceptance,
        requiresPrivacyAcceptance: data.requiresPrivacyAcceptance, // Asumiendo que es lo mismo, ajustar si es diferente
      }

      // Guardar token y datos del usuario
      this.setToken(data.token)
      this.setUser(user)
      this.setMustChangePassword(mustChangePassword)

      // Guardar personType separadamente para evitar bugs de flujo
      this.setUserPersonType(data.personType)

      return {
        user,
        token: data.token,
        mustChangePassword,
      }
    } catch (error: any) {
      // Manejar errores específicos del login
      if (error.response?.status === 401) {
        throw new Error('Credenciales incorrectas')
      } else if (error.response?.status === 404) {
        throw new Error('Usuario no encontrado')
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      } else {
        throw new Error('Error al iniciar sesión')
      }
    }
  }

  async forgotPassword(
    email: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post('/recover-password', { email })
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al enviar el correo')
      } else {
        return response.data
      }
    } catch (error: any) {
      console.error('❌ Error sending forgot password email:', error)
      throw new Error('Error al enviar el correo de recuperación de contraseña')
    }
  }

  async acceptTermsPrivacy(
    type: 'terms' | 'privacy'
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(
        `/legal-documents/accept?type=${type}&platform=web`
      )
      if (!response.data.success) {
        throw new Error(
          response.data.message || 'Error al aceptar términos y condiciones'
        )
      } else {
        return response.data
      }
    } catch (error: any) {
      console.error('❌ Error aceptando términos y condiciones:', error)
      throw new Error('Error al aceptar términos y condiciones')
    }
  }

  // ¿La credit application en curso es la única (primera) que el usuario
  // ha creado? El candado de progressURL ya NO depende de esto (se activa
  // para cualquier solicitud, ver getCreditApplicationProgress/
  // updateCreditApplicationProgress) — este método sigue usándose solo
  // para decidir si el recorrido de onboarding se muestra completo (con
  // stepper) o abreviado (ver authService.isCurrentCreditApplicationTheFirst
  // y las páginas de onboarding que la consumen).
  isFirstCreditApplication(creditApps: CreditApplicationSummary[]): boolean {
    return creditApps.length <= 1
  }

  // ¿La credit application activa es la primera que el usuario ha creado?
  // Se usa en las páginas de onboarding para decidir si el recorrido debe
  // mostrarse completo (con stepper) o abreviado (datos + documentos +
  // pantalla de revisión) para clientes que ya tuvieron una solicitud
  // previa — envuelve el fetch + isFirstCreditApplication de arriba.
  async isCurrentCreditApplicationTheFirst(): Promise<boolean> {
    const creditApps = await this.getAllCreditApplications()
    return this.isFirstCreditApplication(creditApps)
  }

  // ¿Es solicitud repetida (2da+) y qué pantallas del recorrido abreviado
  // corresponde mostrar (consultasStatus.screens)? Un solo fetch para
  // ambas cosas. Si falla, se asume primera solicitud (flujo completo).
  async getRepeatFlowInfo(): Promise<{
    isRepeat: boolean
    screens: Record<string, boolean> | null
  }> {
    const { creditApps, consultasStatus } =
      await this.getCreditApplicationsOverview()
    return {
      isRepeat: !this.isFirstCreditApplication(creditApps),
      screens: consultasStatus?.screens ?? null,
    }
  }

  // Actualiza el "estatus" comercial de la cotización/prospecto (seguimiento
  // de ventas, distinto del progressURL) — mismo PATCH que hoy repiten
  // ApplicationStartPage/CreditBureauConsultationPage/
  // PersonalCreditBureauConsultationPage/CiecConsultationPage, aquí
  // centralizado para el punto de entrada del flujo abreviado (que se salta
  // ApplicationStartPage, la pantalla que normalmente lo dispara). No lanza
  // si falta el id de la cotización o el token — mismo criterio "no
  // bloquear el flujo si esto falla" que ya usan esas pantallas.
  async updateProspectoQuoteEstatus(estatus: string): Promise<void> {
    const prospectoQuoteId = localStorage.getItem('prospectoQuoteId')
    const token = this.getToken()
    if (!prospectoQuoteId || !token) return

    await apiClient.patch(
      `/cotizador/mis-cotizaciones-prospecto/${prospectoQuoteId}/estatus`,
      { estatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
  }

  // Obtener progress URL de credit application
  async getCreditApplicationProgress(): Promise<string | null> {
    try {
      console.log('🔍 Getting credit application progress...')

      const token = this.getToken()
      if (!token) {
        console.log('❌ No token available for credit application request')
        return null
      }

      const response = await apiClient.get('/credit-application', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log('📡 Credit application response:', response.data)

      if (response.data.success && response.data.data.creditApps?.length > 0) {
        const creditApps = response.data.data.creditApps
        const progressURL = creditApps[0].progressURL
        console.log('✅ Progress URL found:', progressURL)

        // El candado de progreso se activa siempre que haya progressURL,
        // sin importar si es la 1ra solicitud o una posterior — a partir
        // de que el flujo abreviado (2da solicitud en adelante) también
        // guarda un progressURL real, tiene sentido reforzarlo igual.
        if (progressURL) {
          this.setActiveProgressURL(progressURL)
          return progressURL
        }

        this.clearActiveProgress()
        return null
      } else {
        console.log('📝 No credit applications found or no progress URL')
        // Limpiar cualquier progreso activo si no hay credit applications
        this.clearActiveProgress()
        return null
      }
    } catch (error: any) {
      console.error('❌ Error getting credit application progress:', error)

      // Si es 401, el token expiró
      if (error.response?.status === 401) {
        console.log('🔑 Token expired, logging out...')
        this.logout()
      }

      return null
    }
  }

  // Obtener ID de credit application actual
  async getCurrentCreditApplicationId(): Promise<string | null> {
    try {
      console.log('🔍 Getting current credit application ID...')

      const token = this.getToken()
      if (!token) {
        console.log('❌ No token available for credit application ID request')
        return null
      }

      const response = await apiClient.get('/credit-application', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log('📡 Credit application ID response:', response.data)

      if (response.data.success && response.data.data.creditApps?.length > 0) {
        const creditApp = response.data.data.creditApps[0]
        const creditAppId = creditApp.id
        const prospectoQuoteId = creditApp.prospectoQuote?.id

        console.log('✅ Credit Application ID found:', creditAppId)
        console.log('✅ Prospecto Quote ID found:', prospectoQuoteId)

        // Guardar prospectoQuoteId en localStorage
        if (prospectoQuoteId) {
          localStorage.setItem('prospectoQuoteId', prospectoQuoteId)
        }

        return creditAppId
      } else {
        console.log('📝 No credit applications found')
        localStorage.removeItem('prospectoQuoteId')
        return null
      }
    } catch (error: any) {
      console.error('❌ Error getting credit application ID:', error)

      // Si es 401, el token expiró
      if (error.response?.status === 401) {
        console.log('🔑 Token expired, logging out...')
        this.logout()
      }

      return null
    }
  }

  // Obtener toda la información de la credit application actual
  async getCurrentCreditApplication() {
    try {
      console.log('🔍 Getting current credit application info...')

      const token = this.getToken()
      if (!token) {
        console.log('❌ No token available for credit application request')
        return null
      }

      const response = await apiClient.get('/credit-application', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success && response.data.data.creditApps?.length > 0) {
        return response.data.data.creditApps[0]
      }
      return null
    } catch (error: any) {
      console.error('❌ Error getting credit application:', error)
      return null
    }
  }

  // Obtener el historial completo de credit applications del usuario (no
  // solo la más reciente). El backend garantiza que el array viene
  // ordenado con la más reciente primero (creditApps[0]), pero a
  // diferencia de getCurrentCreditApplication() este método expone TODO
  // el historial — útil para validar contra cotizaciones ya usadas en
  // cualquier solicitud pasada, sin importar su estatus.
  async getAllCreditApplications(): Promise<CreditApplicationSummary[]> {
    const { creditApps } = await this.getCreditApplicationsOverview()
    return creditApps
  }

  // Mismo GET /credit-application que getAllCreditApplications, pero
  // también expone consultasStatus (null si el backend no lo envía o la
  // petición falla) para no pedir el endpoint dos veces.
  async getCreditApplicationsOverview(): Promise<{
    creditApps: CreditApplicationSummary[]
    consultasStatus: ConsultasStatus | null
  }> {
    try {
      const token = this.getToken()
      if (!token) return { creditApps: [], consultasStatus: null }

      const response = await apiClient.get('/credit-application', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (
        response.data.success &&
        Array.isArray(response.data.data.creditApps)
      ) {
        return {
          creditApps: response.data.data.creditApps,
          consultasStatus: response.data.data.consultasStatus ?? null,
        }
      }
      return { creditApps: [], consultasStatus: null }
    } catch (error: any) {
      console.error('❌ Error getting all credit applications:', error)
      if (error.response?.status === 401) {
        this.logout()
      }
      return { creditApps: [], consultasStatus: null }
    }
  }

  // Obtener las credit applications EN PROCESO del usuario (arrendamientos
  // activos) — alimenta la tab "Activos" de "Mis arrendamientos". Mismo
  // patrón de manejo de token/errores que getAllCreditApplications().
  async getInProgressCreditApplications(): Promise<
    InProgressLeaseApplication[]
  > {
    try {
      const token = this.getToken()
      if (!token) return []

      const response = await apiClient.get('/credit-application/in-progress', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (
        response.data.success &&
        Array.isArray(response.data.data.creditApps)
      ) {
        return response.data.data.creditApps
      }
      return []
    } catch (error: any) {
      console.error('❌ Error getting in-progress credit applications:', error)
      if (error.response?.status === 401) {
        this.logout()
      }
      return []
    }
  }

  // Obtener las credit applications RECHAZADAS del usuario — alimenta la
  // tab "Rechazados" de "Mis arrendamientos". Mismo patrón de manejo de
  // token/errores que getInProgressCreditApplications().
  async getRejectedCreditApplications(): Promise<
    InProgressLeaseApplication[]
  > {
    try {
      const token = this.getToken()
      if (!token) return []

      const response = await apiClient.get('/credit-application/rejected', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (
        response.data.success &&
        Array.isArray(response.data.data.creditApps)
      ) {
        return response.data.data.creditApps
      }
      return []
    } catch (error: any) {
      console.error('❌ Error getting rejected credit applications:', error)
      if (error.response?.status === 401) {
        this.logout()
      }
      return []
    }
  }

  // Obtener la tabla de amortización de la cotización final de una credit
  // application — alimenta la tab "Tabla de amortización" de "Detalle de
  // arrendamiento". Mismo patrón de manejo de token/errores que
  // getInProgressCreditApplications().
  async getAmortizationTable(
    creditApplicationId: string
  ): Promise<AmortizationTable | null> {
    try {
      const token = this.getToken()
      if (!token) return null

      const response = await apiClient.get(
        `/credit-application/${creditApplicationId}/cotizaciones/nueva/tabla-amortizacion`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success && response.data.data?.tabla_amortizacion) {
        return response.data.data.tabla_amortizacion
      }
      return null
    } catch (error: any) {
      console.error('❌ Error getting amortization table:', error)
      if (error.response?.status === 401) {
        this.logout()
      }
      return null
    }
  }

  /**
   * Finaliza una solicitud de crédito manualmente (bypass de webhook)
   */
  async finalizeCreditApplicationManual(creditAppId: string): Promise<any> {
    try {
      console.log(`🚀 Finalizing credit application ${creditAppId} manually...`)
      const response = await apiClient.patch(
        `/credit-application/${creditAppId}/finalize-manual`
      )
      console.log('✅ Finalize response:', response.data)

      if (response.data && response.data.success === false) {
        throw new Error(
          response.data.message ||
            'Error al finalizar la solicitud en el servidor'
        )
      }

      return response.data
    } catch (error: any) {
      console.error('❌ Error finalizing credit application manually:', error)
      const errorMessage =
        error.response?.data?.message || error.message || 'Error desconocido'
      throw new Error(errorMessage)
    }
  }

  // Actualizar progress URL de credit application
  async updateCreditApplicationProgress(
    creditAppId: string,
    newProgressURL: string
  ): Promise<boolean> {
    try {
      console.log('🔄 Updating credit application progress...')
      console.log('📍 Credit App ID:', creditAppId)
      console.log('📍 New Progress URL:', newProgressURL)

      const token = this.getToken()
      if (!token) {
        console.log('❌ No token available for progress update request')
        return false
      }

      const response = await apiClient.patch(
        `/credit-application/${creditAppId}/progress-url`,
        {
          progressURL: newProgressURL,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      console.log('📡 Progress update response:', response.data)

      if (response.data.success) {
        console.log('✅ Progress URL updated successfully in backend')

        // El candado local (localStorage, leído por ProgressGuard) se
        // mantiene encendido para cualquier solicitud, no solo la 1ra — el
        // flujo abreviado a partir de la 2da también tiene un progressURL
        // real que vale la pena reforzar.
        console.log('🔄 Updating local progress URL to:', newProgressURL)
        this.setActiveProgressURL(newProgressURL)
        console.log('✅ Local progress URL updated successfully')

        return true
      } else {
        console.log('❌ Failed to update progress URL')
        throw new Error(
          response.data.message ||
            'Error al actualizar el progreso en el servidor'
        )
      }
    } catch (error: any) {
      console.error('❌ Error updating credit application progress:', error)

      // Si es 401, el token expiró
      if (error.response?.status === 401) {
        console.log('🔑 Token expired, logging out...')
        this.logout()
      }

      throw new Error(
        error.response?.data?.message ||
          error.message ||
          'Error al actualizar el progreso'
      )
    }
  }

  // Realizar logout
  logout(): void {
    this.removeToken()
    this.removeUser()
    this.removeMustChangePassword()
    this.removeUserPersonType()
  }

  // Verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    const token = this.getToken()
    return !!token && !this.isTokenExpired(token)
  }

  // Obtener token del localStorage
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  // Guardar token en localStorage
  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  // Remover token del localStorage
  private removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY)
  }

  // Obtener datos del usuario del localStorage
  getUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY)
    if (!userData) return null

    try {
      return JSON.parse(userData)
    } catch {
      return null
    }
  }

  // Guardar datos del usuario en localStorage
  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
  }

  // Remover datos del usuario del localStorage
  private removeUser(): void {
    localStorage.removeItem(this.USER_KEY)
  }

  // Guardar mustChangePassword en localStorage
  setMustChangePassword(value: boolean) {
    localStorage.setItem(
      this.MUST_CHANGE_PASSWORD_KEY,
      value ? 'true' : 'false'
    )
  }

  // Obtener mustChangePassword del localStorage
  getMustChangePassword(): boolean {
    return localStorage.getItem(this.MUST_CHANGE_PASSWORD_KEY) === 'true'
  }

  // Remover mustChangePassword del localStorage
  removeMustChangePassword() {
    localStorage.removeItem(this.MUST_CHANGE_PASSWORD_KEY)
  }

  // Guardar userPersonType en localStorage
  private setUserPersonType(personType: string): void {
    localStorage.setItem('userPersonType', personType)
    console.log('💾 PersonType saved to localStorage:', personType)
  }

  // Obtener userPersonType del localStorage
  getUserPersonType(): string | null {
    return localStorage.getItem('userPersonType')
  }

  // Remover userPersonType del localStorage
  private removeUserPersonType(): void {
    localStorage.removeItem('userPersonType')
  }

  // Verificar si el token ha expirado
  private isTokenExpired(token: string): boolean {
    try {
      // Decodificar el payload del JWT sin verificar la firma
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000

      return payload.exp < currentTime
    } catch {
      // Si no se puede decodificar, considerar como expirado
      return true
    }
  }

  // Renovar token si está próximo a expirar
  async refreshTokenIfNeeded(): Promise<void> {
    const token = this.getToken()
    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      const timeUntilExpiry = payload.exp - currentTime

      // Si el token expira en menos de 5 minutos, intentar renovarlo
      if (timeUntilExpiry < 300) {
        // Aquí podrías implementar un endpoint de refresh token si existe
        console.log(
          'Token próximo a expirar, considerar implementar refresh token'
        )
      }
    } catch {
      // Si hay error al decodificar, logout
      this.logout()
    }
  }

  // Cambiar contraseña del usuario
  async updatePassword(
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    const token = this.getToken()
    if (!token) throw new Error('No autenticado')
    await apiClient.post(
      '/update-password',
      { newPassword, confirmPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    // Después de cambiar la contraseña, forzar mustChangePassword a false en el front
    this.setMustChangePassword(false)
  }

  // ========================================
  // PROGRESS URL BLOCKING METHODS
  // ========================================

  // Guardar progressURL activo
  setActiveProgressURL(progressURL: string): void {
    localStorage.setItem(this.PROGRESS_URL_KEY, progressURL)
    localStorage.setItem(this.HAS_ACTIVE_PROGRESS_KEY, 'true')
    console.log('🔒 Progress URL set as active:', progressURL)
  }

  // Obtener progressURL activo
  getActiveProgressURL(): string | null {
    return localStorage.getItem(this.PROGRESS_URL_KEY)
  }

  // Verificar si hay progreso activo
  hasActiveProgress(): boolean {
    return localStorage.getItem(this.HAS_ACTIVE_PROGRESS_KEY) === 'true'
  }

  // Limpiar progreso activo
  clearActiveProgress(): void {
    localStorage.removeItem(this.PROGRESS_URL_KEY)
    localStorage.removeItem(this.HAS_ACTIVE_PROGRESS_KEY)
    console.log('🔓 Active progress cleared')
  }

  // Verificar si usuario está en la URL correcta
  isUserOnCorrectProgressURL(): boolean {
    const activeProgressURL = this.getActiveProgressURL()
    const currentPath = window.location.pathname + window.location.search

    console.log('🔍 Checking progress URL compliance...')
    console.log('📍 Active progress URL:', activeProgressURL)
    console.log('📍 Current path:', currentPath)

    if (!activeProgressURL || !this.hasActiveProgress()) {
      console.log('✅ No active progress, user can navigate freely')
      return true
    }

    const isCompliant = currentPath === activeProgressURL
    console.log(
      isCompliant
        ? '✅ User is on correct progress URL'
        : '❌ User is NOT on correct progress URL'
    )
    return isCompliant
  }

  // Verificar si el usuario debe estar bloqueado en pending-proposals
  // Caso específico: NO tiene credit-application (independiente de mustChangePassword)
  // PERO respetando la funcionalidad de mustChangePassword existente
  async shouldBeRestrictedToPendingProposals(): Promise<boolean> {
    try {
      console.log(
        '🔍 Checking if user should be restricted to pending-proposals...'
      )

      // Verificar estado actual
      const mustChangePassword = this.getMustChangePassword()
      console.log(
        '🔑 mustChangePassword from localStorage:',
        mustChangePassword
      )

      const userData = this.getUser()
      console.log('👤 User data:', userData)

      // IMPORTANTE: Si mustChangePassword = true, NO aplicar esta restricción
      // Dejar que la funcionalidad de cambio de contraseña funcione normalmente
      if (mustChangePassword) {
        console.log(
          '🔑 mustChangePassword is true, letting password change flow handle navigation'
        )
        return false
      }

      console.log(
        '� mustChangePassword is false, checking credit-application...'
      )

      // Verificar si tiene credit-application
      const creditAppId = await this.getCurrentCreditApplicationId()
      console.log('💳 Credit application ID found:', creditAppId)

      if (creditAppId) {
        console.log(
          '✅ User has credit-application, no restriction to pending-proposals'
        )
        return false
      }

      console.log('🚫 User has NO credit-application')
      console.log(
        '📍 User should be restricted to /onboarding/pending-proposals'
      )
      return true
    } catch (error) {
      console.error('❌ Error checking pending-proposals restriction:', error)
      // En caso de error, asumir que no hay restricción para no bloquear al usuario
      return false
    }
  }

  // Sincronizar el progressURL local con el backend
  async syncProgressURLWithBackend(): Promise<void> {
    try {
      console.log('🔄 Syncing local progressURL with backend...')
      const backendProgressURL = await this.getCreditApplicationProgress()

      if (backendProgressURL) {
        const localProgressURL = this.getActiveProgressURL()

        if (localProgressURL !== backendProgressURL) {
          console.log('📍 Local and backend progressURL differ:')
          console.log('📍 Local:', localProgressURL)
          console.log('📍 Backend:', backendProgressURL)
          console.log('🔄 Updating local to match backend...')

          this.setActiveProgressURL(backendProgressURL)
          console.log('✅ Local progressURL synced with backend')
        } else {
          console.log('✅ Local and backend progressURL are in sync')
        }
      } else {
        console.log('📝 No backend progressURL, clearing local')
        this.clearActiveProgress()
      }
    } catch (error) {
      console.error('❌ Error syncing progressURL with backend:', error)
    }
  }

  // Verificar si el usuario está en pending-proposals cuando debería estar ahí
  isUserOnPendingProposalsWhenRequired(): boolean {
    const currentPath = window.location.pathname
    return currentPath === '/onboarding/pending-proposals'
  }
}

export const authService = new AuthService()
export default authService
